import type {
  PuppyTopologyRuntimeDocument,
  PuppyVisibleBodyTopologyAsset,
  PuppyVisibleModelExportRoot,
} from './types'
import { PUPPY_PACKAGE_VERSION } from './types'

/**
 * 读取可选字符串，避免把空值写入拓扑 runtime。
 *
 * @param value 候选值。
 * @returns 规范化后的非空字符串。
 */
function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
}

/**
 * 克隆面到三角形范围，避免导出文档持有运行时 MeshData 的可变引用。
 *
 * @param ranges MeshData 中的面三角范围。
 * @returns 可安全序列化的范围数组。
 */
function cloneFaceTriangleRanges(ranges: [number, number][] | undefined): [number, number][] | undefined {
  return ranges && ranges.length > 0
    ? ranges.map((range) => [range[0], range[1]])
    : undefined
}

/**
 * 判断显式面三角范围是否与拓扑快照完全一致。
 *
 * 设计说明：
 * 1. `topologySnapshot.faces[].triangleRange` 已经能表达同一份映射时，asset 级数组属于重复数据；
 * 2. 只在两者不一致时保留显式数组，避免大型装配体为每个面重复写两个整数。
 *
 * @param ranges MeshData 中的面三角范围。
 * @param snapshot 拓扑快照。
 * @returns 是否可由拓扑快照无损还原。
 */
function areFaceTriangleRangesEquivalentToSnapshot(
  ranges: [number, number][] | undefined,
  snapshot: TopologySnapshot,
): boolean {
  if (!ranges || ranges.length !== snapshot.faces.length) {
    return false
  }

  return ranges.every((range, index) => {
    const snapshotRange = snapshot.faces[index]?.triangleRange
    return Array.isArray(snapshotRange)
      && snapshotRange[0] === range[0]
      && snapshotRange[1] === range[1]
  })
}

/**
 * 读取 body 语义 ID。
 *
 * @param asset body 级拓扑资产。
 * @returns body 语义 ID。
 */
function resolveBodyId(asset: PuppyVisibleBodyTopologyAsset): string | undefined {
  return readOptionalString(asset.meshData.metadata?.bodyId)
}

/**
 * 创建可持久化的拓扑元数据。
 *
 * `meshId` 是 display.glb 每次装载后重新建立的运行时投影身份，导入端会依据当前
 * display binding 重新生成；若把它写回 topology-runtime，同一业务场景重载后会产生
 * 不同的离线包内容。其余业务元数据保持原样，并由当前导出事务覆盖权威绑定字段。
 *
 * @param asset body 级拓扑资产。
 * @returns 不包含本次运行时显示身份的持久化元数据。
 */
function createPersistedTopologyMetadata(
  asset: PuppyVisibleBodyTopologyAsset,
): Record<string, unknown> {
  const { meshId: _runtimeMeshId, ...persistedMetadata } = asset.meshData.metadata ?? {}
  return {
    ...persistedMetadata,
    objectId: asset.geometryObjectId,
    bodyBindingId: asset.bodyBindingId,
    source: 'puppy-topology-runtime',
  }
}

/**
 * 序列化 body 级拓扑语义。
 *
 * 设计说明：
 * 1. `.puppy` 的显示网格统一由 `display.glb` 承载，拓扑 runtime 不再重复写顶点/法线/索引；
 * 2. 导入端通过同一个 bodyBindingId 把 GLB 几何与这里的拓扑快照重新组合成 MeshData；
 * 3. 这样可以在不牺牲导入速度的前提下降低包体积，后续如需更紧凑编码也只需替换二进制 codec。
 *
 * @param asset body 级拓扑资产。
 * @returns 可写入二进制拓扑 runtime 的资产 payload。
 */
function serializeTopologyRuntimeAsset(asset: PuppyVisibleBodyTopologyAsset): PuppyTopologyRuntimeDocument['assets'][number] {
  const { meshData } = asset
  if (!meshData.topologySnapshot) {
    throw new Error(`离线包拓扑资产缺少 topologySnapshot: ${asset.bodyBindingId}`)
  }

  const bodyId = resolveBodyId(asset)
  const clonedFaceTriangleRanges = cloneFaceTriangleRanges(meshData.faceTriangleRanges)
  const faceTriangleRanges = areFaceTriangleRangesEquivalentToSnapshot(clonedFaceTriangleRanges, meshData.topologySnapshot)
    ? undefined
    : clonedFaceTriangleRanges

  return {
    bodyBindingId: asset.bodyBindingId,
    geometryObjectId: asset.geometryObjectId,
    ...(asset.exportDefinitionId ? { exportDefinitionId: asset.exportDefinitionId } : {}),
    ...(bodyId ? { bodyId } : {}),
    ...(faceTriangleRanges ? { faceTriangleRanges } : {}),
    metadata: createPersistedTopologyMetadata(asset),
    topologySnapshot: meshData.topologySnapshot,
  }
}

/**
 * 创建 `.puppy` 交互拓扑快照文档。
 *
 * @param exportRoot 可见模型导出根。
 * @returns 可写入 ZIP 的拓扑 runtime 文档。
 */
export function createPuppyTopologyRuntimeDocument(
  exportRoot: PuppyVisibleModelExportRoot,
): PuppyTopologyRuntimeDocument {
  return {
    version: PUPPY_PACKAGE_VERSION,
    assets: exportRoot.bodyTopologyAssets.map(serializeTopologyRuntimeAsset),
  }
}
